/**
 * Auth Store - Autenticazione pulita (zero ARCA!)
 * Per Gleem Core
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { apiClient } from '@/core/services/api'

/**
 * Ruoli utente Gleem Thermo
 *
 * ADMIN: Accesso completo + impostazioni sistema (Andreas)
 * MANAGER: Accesso completo operativo (Enzo)
 * OFFICE: Accesso completo operativo - contabilità/ufficio (Simona)
 * TECHNICIAN: Solo operativo - interventi, calendario, clienti, impianti (Alfonso)
 * USER: Utente base (legacy)
 * GUEST: Solo lettura (legacy)
 */
export type UserRole = 'admin' | 'manager' | 'office' | 'technician' | 'user' | 'guest'

export interface User {
  id: number
  username: string
  email: string
  first_name?: string
  last_name?: string
  full_name?: string
  role: UserRole
  is_active: boolean
  must_change_password?: boolean
}

export interface TenantInfo {
  id: number
  slug: string
  name: string
  tier: string
  status?: string
  trial_ends_at?: string | null
  email_verified?: boolean
  onboarding_status?: Record<string, unknown>
  enabled_features: string[]
  custom_limits: Record<string, number>
}

// Definizione permessi per ruolo
// true = ha accesso, false = non ha accesso
export const ROLE_PERMISSIONS = {
  // Sezioni menu
  dashboard: ['admin', 'manager', 'office', 'technician', 'user'],
  anagrafiche_base: ['admin', 'manager', 'office', 'technician', 'user'], // Clienti, Impianti
  anagrafiche_full: ['admin', 'manager', 'office'], // + Fornitori, Prodotti, Magazzino, Dipendenti
  interventi: ['admin', 'manager', 'office', 'technician', 'user'],
  commerciale: ['admin', 'manager', 'office'],
  contabilita: ['admin', 'manager', 'office'],
  crm: ['admin', 'manager', 'office'],
  report: ['admin', 'manager', 'office'],
  impostazioni: ['admin'], // Solo admin
} as const

function loadTenantFromStorage(): TenantInfo | null {
  try {
    const raw = localStorage.getItem('tenant')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.id === 'number' && typeof parsed.slug === 'string') {
      return parsed
    }
    localStorage.removeItem('tenant')
    return null
  } catch {
    localStorage.removeItem('tenant')
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(localStorage.getItem('token') || null)
  const user = ref<User | null>(null)
  const tenant = ref<TenantInfo | null>(loadTenantFromStorage())
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Promise per attendere il caricamento iniziale dell'utente
  let userLoadPromise: Promise<void> | null = null

  // Getters - Ruoli
  const isAuthenticated = computed(() => !!token.value)
  const tenantSlug = computed(() => tenant.value?.slug || 'default')
  const tenantTier = computed(() => tenant.value?.tier || 'free')
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isManager = computed(() => user.value?.role === 'manager')
  const isOffice = computed(() => user.value?.role === 'office')
  const isTechnician = computed(() => user.value?.role === 'technician')

  // Helper: verifica se l'utente ha accesso a una sezione
  const hasPermission = (section: keyof typeof ROLE_PERMISSIONS): boolean => {
    if (!user.value) return false
    return ROLE_PERMISSIONS[section].includes(user.value.role)
  }

  // Helper: verifica se l'utente ha accesso completo (admin, manager, office)
  const hasFullAccess = computed(() => {
    return user.value?.role === 'admin' ||
      user.value?.role === 'manager' ||
      user.value?.role === 'office'
  })

  // Actions
  async function login(
    username_or_email: string,
    password: string,
    rememberMe: boolean = false,
    tenantSlugParam: string = 'default'
  ) {
    isLoading.value = true
    error.value = null

    try {
      // OAuth2PasswordRequestForm richiede form-urlencoded
      const formData = new URLSearchParams()
      formData.append('username', username_or_email)
      formData.append('password', password)
      formData.append('tenant_slug', tenantSlugParam)
      if (rememberMe) {
        formData.append('remember_me', 'true')
      }

      const response = await apiClient.post('/api/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })

      if (response.data.access_token) {
        token.value = response.data.access_token
        localStorage.setItem('token', response.data.access_token)

        if (response.data.user) {
          user.value = response.data.user
        } else {
          await fetchUser()
        }

        // Store tenant info from login response (FASE 2)
        if (response.data.tenant) {
          tenant.value = response.data.tenant
          localStorage.setItem('tenant', JSON.stringify(response.data.tenant))
        }

        return { success: true }
      }

      throw new Error('Login failed - no access token received')
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        error.value = err.response?.data?.detail || err.response?.data?.message || err.message || 'Login failed'
      } else if (err instanceof Error) {
        error.value = err.message || 'Login failed'
      } else {
        error.value = 'Login failed'
      }
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    token.value = null
    user.value = null
    tenant.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('tenant')
  }

  async function fetchUser() {
    if (!token.value) return

    try {
      const response = await apiClient.get('/api/auth/me')
      user.value = response.data

      // /me now returns tenant info — store it if present and not already set
      if (response.data.tenant && !tenant.value) {
        tenant.value = response.data.tenant
        localStorage.setItem('tenant', JSON.stringify(response.data.tenant))
      }
    } catch (err) {
      console.error('Failed to fetch user:', err)
      await logout()
    }
  }

  /**
   * Attende che l'utente sia caricato (usato dal router per RBAC)
   * Se non c'è token, ritorna immediatamente
   */
  async function waitForUser(): Promise<void> {
    if (!token.value) return
    if (user.value) return

    // Se c'è un promise pendente, attendi
    if (userLoadPromise) {
      await userLoadPromise
      return
    }

    // Se abbiamo un token ma nessun utente e nessuna promise,
    // significa che il login è appena avvenuto ma l'utente non è stato caricato
    // (questo può succedere se la response del login non includeva l'utente)
    if (token.value && !user.value) {
      await fetchUser()
    }
  }

  /**
   * Handle external auth (from Bizcraft signup redirect).
   * Stores token + tenant, fetches user profile.
   */
  async function handleExternalAuth(
    accessToken: string,
    tenantData?: TenantInfo
  ): Promise<boolean> {
    try {
      token.value = accessToken
      localStorage.setItem('token', accessToken)

      if (tenantData) {
        tenant.value = tenantData
        localStorage.setItem('tenant', JSON.stringify(tenantData))
      }

      await fetchUser()
      return !!user.value
    } catch {
      await logout()
      return false
    }
  }

  // Verifica token all'avvio
  if (token.value) {
    userLoadPromise = fetchUser()
  }

  return {
    // State
    token,
    user,
    tenant,
    isLoading,
    error,
    // Computed - Auth
    isAuthenticated,
    // Computed - Tenant
    tenantSlug,
    tenantTier,
    // Computed - Roles
    isAdmin,
    isManager,
    isOffice,
    isTechnician,
    hasFullAccess,
    // Methods
    hasPermission,
    login,
    logout,
    fetchUser,
    waitForUser,
    handleExternalAuth
  }
})



